import React, { useEffect, useMemo, useState, useRef } from "react";
import { taskApi } from "api/tasks/taskApi";

// --- Utility: Normalize Status ---
const normalizeStatus = (value) => {
  if (!value) return "Ongoing";
  const v = String(value).trim().toLowerCase();
  if (v === "completed" || v === "complete") return "Complete";
  if (v === "not started" || v === "not_started" || v === "notstart")
    return "Not started";
  if (v === "overdue") return "Overdue";
  if (v === "halted") return "Halted";
  return "Ongoing";
};

const calculateOverdueDays = (endDate) => {
  if (!endDate) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(endDate);
  due.setHours(0, 0, 0, 0);
  const diffTime = today - due;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

const hasAssignedDates = (task) => {
  const candidates = [
    task?.start_date,
    task?.end_date,
    task?.startDate,
    task?.endDate,
    task?.dueDate,
    task?.due_date,
  ];
  return candidates.some((value) => {
    if (value === null || value === undefined) return false;
    return String(value).trim() !== "";
  });
};

const STATUS_COLUMNS = [
  {
    key: "Complete",
    headerClass: "bg-[#D1FAE5] text-[#065F46]",
    title: "Complete",
  },
  {
    key: "Ongoing",
    headerClass: "bg-[#FEF3C7] text-[#92400E]",
    title: "Ongoing",
  },
  // { key: "Overdue", headerClass: "bg-red-600", title: "Overdue" },
  {
    key: "Halted",
    headerClass: "bg-[#FEE2E2] text-[#991B1B]",
    title: "Halted",
  },
  {
    key: "Not started",
    headerClass: "bg-[#F3F4F6] text-[#1F2937]",
    title: "Not started",
  },
];

// --- Component: Drop Indicator ---
// A visual line showing where the task will land
const DropIndicator = ({ isVisible }) => {
  if (!isVisible) return null;
  return (
    <div className="my-1 h-1 w-full bg-purple-600 rounded-full animate-pulse shadow-[0_0_8px_rgba(147,51,234,0.5)]" />
  );
};

// --- Component: Task Card ---
const TaskCard = ({
  task,
  index,
  onDragStart,
  getInitials,
  getAvatarColor,
  siteMembers,
  isExpanded,
  onToggleSubtasks,
  groupId,
  subCount,
  onDragOverCard, // New prop
}) => {
  const cardRef = useRef(null);
  const isLocked =
    normalizeStatus(task.status) === "Not started" && !hasAssignedDates(task);

  return (
    <div
      ref={cardRef}
      draggable={!isLocked}
      data-index={index}
      onDragStart={(e) => onDragStart(e, task)}
      // We handle DragOver here to calculate top/bottom halves
      onDragOver={(e) => onDragOverCard(e, index, cardRef)}
      className={`bg-white p-2 mb-2 rounded border shadow-sm transition-shadow select-none relative ${
        isLocked
          ? "opacity-60 cursor-not-allowed"
          : "cursor-move hover:shadow-md"
      }`}
      id={`kanban-task-${task.id}`}
      title={
        isLocked ? "Task has no dates assigned and cannot be moved" : undefined
      }
    >
      <div className="flex items-center justify-between">
        <div className="font-medium text-sm truncate pr-2">{task.name}</div>
        {(subCount > 0 ||
          (Array.isArray(task.subtasks) && task.subtasks.length > 0)) && (
          <button
            aria-label="Toggle subtasks"
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onToggleSubtasks === "function")
                onToggleSubtasks(groupId, task.id);
            }}
            className="text-xs px-2 py-0.5 rounded border bg-gray-100 hover:bg-gray-200"
            title={`${subCount ?? (task.subtasks?.length || 0)} subtasks`}
          >
            {subCount ?? (task.subtasks?.length || 0)}
          </button>
        )}
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="flex -space-x-1">
          {Array.isArray(task.members) && task.members.length > 0 ? (
            <>
              {task.members.slice(0, 3).map((m) => {
                const fullName = `${m.first_name} ${m.last_name}`;
                const memberPic =
                  siteMembers?.find((sm) => sm.member_id === m.member_id)
                    ?.userpic ?? m?.userpic;

                return memberPic === "" || !memberPic ? (
                  <span
                    key={m.member_id}
                    className="flex flex-row items-center justify-center w-6 h-6 rounded-full bg-[#343C6A] text-white font-medium text-[10px] border-2 border-white"
                    title={fullName}
                  >
                    {getInitials(fullName)}
                  </span>
                ) : (
                  <img
                    key={m.member_id}
                    src={memberPic}
                    alt={fullName}
                    className="w-6 h-6 rounded-full border-2 border-white object-cover"
                    title={fullName}
                  />
                );
              })}

              {task.members.length > 3 && (
                <span
                  className="flex flex-row items-center justify-center w-6 h-6 rounded-full bg-[#343C6A] text-white font-medium text-[10px] border-2 border-white"
                  title={`+${task.members.length - 3} more members`}
                >
                  +{task.members.length - 3}
                </span>
              )}
            </>
          ) : (
            <div className="text-xs text-gray-400">Unassigned</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {normalizeStatus(task.status) !== "Complete" &&
            calculateOverdueDays(task.end_date) > 0 && (
              <div className="bg-red-100 text-red-600 px-1.5 py-0.5 rounded text-[9px] font-semibold border border-red-200">
                Overdue by {calculateOverdueDays(task.end_date)} days
              </div>
            )}
          <div className="flex items-center text-[10px] text-gray-600">
            <span>{task.dueDate || ""}</span>
          </div>
        </div>
      </div>

      {isExpanded &&
        Array.isArray(task.subtasks) &&
        task.subtasks.length > 0 && (
          <div className="mt-2 pt-2 border-t">
            {task.subtasks.map((st, index) => {
              const isLast = index === task.subtasks.length - 1;
              return (
                <div key={st.id} className="flex">
                  <div className="relative w-4 flex-shrink-0 flex flex-col items-center">
                    <div
                      className="w-[2px] bg-gray-300"
                      style={{
                        height: isLast
                          ? index === 0
                            ? "calc(50% + 0.25rem)"
                            : "calc(50% - 0.25rem)"
                          : index === 0
                            ? "calc(100% + 0.5rem)"
                            : "100%",
                        marginTop: index === 0 ? "-0.5rem" : undefined,
                      }}
                    />
                    <div className="absolute top-1/2 right-0 w-2 h-[2px] bg-gray-300 -translate-y-1/2 -mt-1" />
                  </div>
                  <div className="flex-grow min-w-0 pb-2">
                    <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="text-xs text-gray-800 truncate pr-2">
                        {st.name}
                      </div>
                      <div className="text-[10px] text-gray-600">
                        {st.dueDate || ""}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
};

// --- Component: Kanban Column ---
const KanbanColumn = ({
  group,
  column,
  tasks,
  onDrop,
  onDragStart,
  getInitials,
  getAvatarColor,
  siteMembers,
  expandedTasks,
  toggleTaskSubtasks,
  subtaskCountById,
  setDragOverInfo,
  dragOverInfo,
}) => {
  const columnRef = useRef(null);

  const handleDragOverColumn = (e) => {
    e.preventDefault();
    // If we are dragging over the empty space at the bottom of the column
    if (e.target === columnRef.current) {
      setDragOverInfo({
        groupId: group.id,
        columnKey: column.key,
        index: tasks.length, // Indicate dropping at the very end
      });
    }
  };

  const handleDragOverCard = (e, index, cardRef) => {
    e.preventDefault();
    e.stopPropagation(); // Stop bubbling to column

    const rect = cardRef.current.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const isTopHalf = e.clientY < midY;

    // Determine insertion index based on top/bottom half
    const targetIndex = isTopHalf ? index : index + 1;

    setDragOverInfo({
      groupId: group.id,
      columnKey: column.key,
      index: targetIndex,
    });
  };

  const isOverThisColumn =
    dragOverInfo?.groupId === group.id &&
    dragOverInfo?.columnKey === column.key;

  return (
    <div className="bg-white rounded-lg shadow h-full flex flex-col">
      <div
        className={`p-2 ${column.headerClass} text-center rounded-t-lg font-semibold text-sm`}
      >
        {column.title} ({tasks.length})
      </div>

      {/* 1. h-full and flex-1 ensure this div takes all available vertical space 
         2. This fixes the "dropping at bottom" issue 
      */}
      <div
        ref={columnRef}
        className={`p-2 min-h-[150px] flex-1 transition-colors duration-200 ${
          isOverThisColumn ? "bg-purple-50" : ""
        }`}
        onDragOver={handleDragOverColumn}
        onDrop={(e) => onDrop(e, group.id, column.key)}
      >
        {/* Drop indicator at position 0 */}
        <DropIndicator
          isVisible={isOverThisColumn && dragOverInfo.index === 0}
        />

        {tasks.map((task, idx) => (
          <React.Fragment key={task.id}>
            <TaskCard
              task={task}
              index={idx}
              onDragStart={onDragStart}
              getInitials={getInitials}
              getAvatarColor={getAvatarColor}
              siteMembers={siteMembers}
              isExpanded={!!expandedTasks[task.id]}
              onToggleSubtasks={toggleTaskSubtasks}
              groupId={group.id}
              subCount={subtaskCountById[task.id]}
              onDragOverCard={handleDragOverCard}
            />
            {/* Drop indicator after this card (if index matches) */}
            <DropIndicator
              isVisible={isOverThisColumn && dragOverInfo.index === idx + 1}
            />
          </React.Fragment>
        ))}

        {/* Helper text if empty and dragging over */}
        {tasks.length === 0 && isOverThisColumn && (
          <div className="h-full w-full border-2 border-dashed border-purple-300 rounded flex items-center justify-center text-purple-400 text-xs">
            Drop here
          </div>
        )}
      </div>
    </div>
  );
};

// --- Main Component: Kanban View ---
const KanbanView = ({
  taskGroups = [],
  refetchTasks,
  onTaskStatusChange,
  onTaskReorder,
  getInitials,
  getAvatarColor,
}) => {
  const [localGroups, setLocalGroups] = useState(() =>
    (taskGroups || []).map((g) => ({ ...g, expanded: !!g.expanded })),
  );
  const [expandedTasks, setExpandedTasks] = useState({});
  const [siteMembers, setSiteMembers] = useState([]);

  // New State: Track where the drop is currently hovering
  // { groupId: string, columnKey: string, index: number }
  const [dragOverInfo, setDragOverInfo] = useState(null);
  const [hasTaskAuthority, setHasTaskAuthority] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // --- Subtask Logic (Unchanged) ---
  const normalizeKanbanSubtask = (raw, groupId) => ({
    id: raw.subtask_id ?? raw.id,
    name: raw.subtask_name ?? raw.name ?? "(no-title)",
    status: raw.is_completed ? "Completed" : "Ongoing",
    subtask_details: raw.subtask_details || "–",
    is_completed: raw.is_completed || false,
    dueDate: raw.end_date || "",
    start_date: raw.start_date || new Date().toISOString().slice(0, 10),
    end_date: raw.end_date || new Date().toISOString().slice(0, 10),
    priority: raw.priority,
    assignee: raw.assign_to || [],
    task_group: groupId,
  });

  const fetchSubtasksIfNeeded = async (groupId, taskId) => {
    try {
      const group = localGroups.find((g) => g.id === groupId);
      const task = group?.tasks?.find((t) => t.id === taskId);
      if (!task) return;
      if (Array.isArray(task.subtasks) && task.subtasks.length > 0) return;
      const rawSubs = await taskApi.getAllSubtasks(taskId);
      const subs = (rawSubs || []).map((st) =>
        normalizeKanbanSubtask(st, groupId),
      );
      setLocalGroups((prev) =>
        prev.map((g) =>
          g.id === groupId
            ? {
                ...g,
                tasks: g.tasks.map((t) =>
                  t.id === taskId ? { ...t, subtasks: subs } : t,
                ),
              }
            : g,
        ),
      );
    } catch (e) {
      console.warn("Failed to fetch subtasks for task", taskId, e);
    }
  };

  const toggleTaskSubtasks = async (groupId, taskId) => {
    await fetchSubtasksIfNeeded(groupId, taskId);
    setExpandedTasks((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const subtaskCountById = useMemo(() => {
    const map = {};
    for (const g of localGroups || []) {
      for (const t of g.tasks || []) {
        if (Array.isArray(t.subtasks) && t.subtasks.length > 0) {
          map[t.id] = t.subtasks.length;
          continue;
        }
        const rawCount = t?.subtasks_summary?.total_subtasks;
        const count =
          typeof rawCount === "number"
            ? rawCount
            : rawCount != null && rawCount !== ""
              ? Number(rawCount)
              : 0;
        map[t.id] = Number.isFinite(count) ? count : 0;
      }
    }
    return map;
  }, [localGroups]);

  useEffect(() => {
    setLocalGroups((prev) => {
      const prevById = new Map(prev.map((g) => [g.id, g]));
      return (taskGroups || []).map((incoming) => {
        const prevMatch = prevById.get(incoming.id);
        return {
          ...incoming,
          expanded: prevMatch ? !!prevMatch.expanded : !!incoming.expanded,
        };
      });
    });
  }, [taskGroups]);

  useEffect(() => {
    if (!Array.isArray(taskGroups)) return;
    const toFetch = taskGroups.filter((g) => !g.tasksFetched);
    if (toFetch.length && typeof refetchTasks === "function") {
      toFetch.forEach((g) => refetchTasks(g.id));
    }
  }, [taskGroups, refetchTasks]);

  // Fetch site members for user pictures (same behavior as MainTableView)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const siteData = JSON.parse(localStorage.getItem("siteData"));
        if (!siteData?.id) return;
        const response = await taskApi.getSiteMembers(siteData.id);
        if (!cancelled && response?.status) {
          setSiteMembers(Array.isArray(response.data) ? response.data : []);
        }
      } catch (e) {
        // Non-blocking: Kanban will fall back to initials
        console.warn("Failed to fetch site members for Kanban:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    const fetchAuthority = async () => {
      try {
        const siteData = JSON.parse(localStorage.getItem("siteData") || "{}");
        if (!siteData?.id) return;
        const response = await taskApi.getSiteMembers(siteData.id);
        if (response?.status) {
          const members = Array.isArray(response.data) ? response.data : [];
          const currentMember = members.find((m) => m.user_id === user.user);
          setHasTaskAuthority(
            currentMember?.create_taskgroup ||
              currentMember?.is_admin ||
              currentMember?.joined_as === "Owner",
          );
        }
      } catch {
        setHasTaskAuthority(false);
      }
    };
    fetchAuthority();
  }, [user.user]);
  // --- Drag and Drop Logic ---

  // const handleDragStart = (e, task) => {
  //   if (normalizeStatus(task.status) === "Not started" && !hasAssignedDates(task)) {
  //     e.preventDefault();
  //     return;
  //   }
  const handleDragStart = (e, task) => {
    if (!hasTaskAuthority) {
      e.preventDefault();
      return;
    }
    if (
      normalizeStatus(task.status) === "Not started" &&
      !hasAssignedDates(task)
    ) {
      e.preventDefault();
      return;
    }
    const payload = JSON.stringify({
      taskId: task.id,
      fromStatus: task.status,
    });
    e.dataTransfer.setData("application/json", payload);
    e.dataTransfer.effectAllowed = "move";
  };

  const toggleGroupExpanded = (groupId) => {
    setLocalGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, expanded: !g.expanded } : g)),
    );
  };

  // const handleDropOnColumn = (e, groupId, columnKey) => {
  //   e.preventDefault();
  //   setDragOverInfo(null); // Reset indicator

  //   const raw = e.dataTransfer.getData("application/json");
  //   if (!raw) return;
  const handleDropOnColumn = (e, groupId, columnKey) => {
    e.preventDefault();
    setDragOverInfo(null);

    if (!hasTaskAuthority) return;

    const raw = e.dataTransfer.getData("application/json");
    if (!raw) return;
    const { taskId, fromStatus } = JSON.parse(raw);

    // Block moves for "Not started" tasks with no dates assigned
    const isLockedTask = localGroups.some((g) =>
      (g.tasks || []).some(
        (t) =>
          t.id === taskId &&
          normalizeStatus(t.status) === "Not started" &&
          !hasAssignedDates(t),
      ),
    );
    if (isLockedTask) return;

    // If dragOverInfo is set, use that index. Otherwise, append (infinity/length).
    const insertIndex = dragOverInfo?.index;

    let reorderInfo = null;

    setLocalGroups((prev) => {
      const groupsCopy = prev.map((g) => ({
        ...g,
        tasks: [...(g.tasks || [])],
      }));
      const gIdx = groupsCopy.findIndex((g) => g.id === groupId);
      if (gIdx === -1) return prev;

      const group = groupsCopy[gIdx];
      const allTasks = group.tasks || [];
      const currentIndex = allTasks.findIndex((t) => t.id === taskId);
      if (currentIndex === -1) return prev;

      const [moved] = allTasks.splice(currentIndex, 1);

      // Update status optimistically
      moved.status = columnKey;

      // Filter tasks only for the target column to determine correct splice position
      const tasksInTargetStatus = allTasks.filter(
        (t) => normalizeStatus(t.status) === columnKey,
      );

      // Determine where to insert in the MAIN array
      // 1. If inserting at end of specific column:
      if (
        insertIndex === undefined ||
        insertIndex >= tasksInTargetStatus.length
      ) {
        allTasks.push(moved);
        reorderInfo = {
          targetColumnIndex: tasksInTargetStatus.length,
          beforeTaskId: null,
        };
      } else {
        // 2. Insert specific index
        const taskAfter = tasksInTargetStatus[insertIndex];
        const indexInMainArray = allTasks.findIndex(
          (t) => t.id === taskAfter.id,
        );

        // Safety check
        if (indexInMainArray !== -1) {
          allTasks.splice(indexInMainArray, 0, moved);
          reorderInfo = {
            targetColumnIndex: insertIndex,
            beforeTaskId: taskAfter.id,
          };
        } else {
          allTasks.push(moved); // Fallback
        }
      }

      groupsCopy[gIdx] = { ...group, tasks: allTasks };
      return groupsCopy;
    });

    const normalizedFrom = normalizeStatus(fromStatus);

    if (
      normalizedFrom !== columnKey &&
      typeof onTaskStatusChange === "function"
    ) {
      onTaskStatusChange(groupId, taskId, columnKey);
    } else if (
      normalizedFrom === columnKey &&
      typeof onTaskReorder === "function"
    ) {
      const { targetColumnIndex, beforeTaskId } = reorderInfo || {};
      onTaskReorder(groupId, taskId, columnKey, {
        targetIndex:
          typeof targetColumnIndex === "number" ? targetColumnIndex : 0,
        beforeTaskId: beforeTaskId || null,
      });
    }
  };

  const groupsToRender = useMemo(
    () => localGroups.filter((g) => Array.isArray(g.tasks)),
    [localGroups],
  );

  return (
    <div className="p-3" onDragOver={(e) => e.preventDefault()}>
      {groupsToRender.map((group) => (
        <div key={group.id} className="mb-8">
          <div
            className={`flex overflow-hidden cursor-pointer rounded-lg group ${
              group.expanded ? "bg-gray-100" : " hover:bg-slate-300 bg-white"
            } `}
            style={{
              borderLeftColor: group.expanded ? "transparent" : "#3B0069",
              borderTopWidth: group.expanded ? "0px" : "1px",
              borderRightWidth: group.expanded ? "0px" : "1px",
              borderBottomWidth: group.expanded ? "0px" : "1px",
              borderTopColor: group.expanded ? "transparent" : "#78797A",
              borderRightColor: group.expanded ? "transparent" : "#78797A",
              borderBottomColor: group.expanded ? "transparent" : "#78797A",
              borderLeftWidth: group.expanded ? "0px" : "4px",
            }}
          >
            <div className="flex-grow px-4 py-2 hover:bg-[#ECE9EE]">
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-4 flex items-center">
                  <button
                    className="mr-2"
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroupExpanded(group.id);
                    }}
                    title={group.expanded ? "Collapse" : "Expand"}
                  >
                    <svg
                      className={`w-5 h-5 transform ${group.expanded ? "rotate-90" : ""}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        d="M9 5l7 7-7 7"
                        strokeWidth={2}
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  <div
                    className="flex flex-row items-center justify-between "
                    style={{ width: group.expanded ? "90%" : "65%" }}
                  >
                    <span className="pl-2 truncate text-[18px] font-medium">
                      {group.name}
                    </span>

                    {Array.isArray(group.tasks) &&
                      group.tasks.length > 0 &&
                      group.expanded && (
                        <span className="ml-2 text-sm text-gray-500 flex items-center">
                          {
                            group.tasks.filter(
                              (t) => normalizeStatus(t.status) === "Complete",
                            ).length
                          }
                          /{group.tasks.length} tasks complete
                        </span>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {group.expanded && (
            <div className="grid grid-cols-4 gap-4 items-start">
              {STATUS_COLUMNS.map((col) => {
                const tasksInCol = (group.tasks || []).filter(
                  (t) => normalizeStatus(t.status) === col.key,
                );
                return (
                  <KanbanColumn
                    key={col.key}
                    group={group}
                    column={col}
                    tasks={tasksInCol}
                    onDrop={handleDropOnColumn}
                    onDragStart={handleDragStart}
                    getInitials={getInitials}
                    getAvatarColor={getAvatarColor}
                    siteMembers={siteMembers}
                    expandedTasks={expandedTasks}
                    toggleTaskSubtasks={toggleTaskSubtasks}
                    subtaskCountById={subtaskCountById}
                    setDragOverInfo={setDragOverInfo}
                    dragOverInfo={dragOverInfo}
                  />
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default KanbanView;
